import { Button, Dialog, Divider, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import type { Invoice } from '../api/api'
import { BorderedStack } from '../components/BorderedStack'

export interface InvoiceSummaryProps {
  invoice: Invoice | undefined
  isLoading: boolean
  isError: boolean
}

export const InvoiceSummary = ({ invoice, isLoading, isError }: InvoiceSummaryProps) => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button
        variant="contained"
        size="medium"
        color={isError ? 'error' : 'primary'}
        loading={isLoading}
        disabled={!invoice}
        onClick={() => setOpen(true)}
      >
        Show invoice
      </Button>
      {invoice && <InvoiceDialog invoice={invoice} open={open} onClose={() => setOpen(false)} />}
    </>
  )
}

interface InvoiceDialogProps {
  open: boolean
  onClose: () => void
  invoice: Invoice
}

const InvoiceDialog = ({ open, onClose, invoice }: InvoiceDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <Stack
        sx={{
          padding: 3,
        }}
      >
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="p1Medium">Invoice Summary</Typography>
            {invoice.nextInvoiceDate && (
              <Stack
                sx={{
                  textAlign: 'right',
                }}
              >
                <Typography variant="p4Regular">Next invoice date</Typography>
                <Typography variant="p3Medium">{invoice.nextInvoiceDate}</Typography>
              </Stack>
            )}
          </Stack>
          <BorderedStack
            sx={{
              gap: 1,
              padding: 1.5,
            }}
          >
            <Stack>
              {invoice?.items.map((item) => (
                <Stack
                  key={item.billableId}
                  sx={{
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="p3Medium">{mapBillableIdToName(item.billableId)}</Typography>
                    <Typography data-testid="fixed-price" variant="p3Regular">
                      {format(item.totalPrice)}
                    </Typography>
                  </Stack>
                  {showDiscount(item.discountAmount) && (
                    <Stack
                      sx={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        paddingLeft: 2,
                      }}
                    >
                      <Typography variant="p3Regular">Discount</Typography>
                      <Typography variant="p3Regular">-{format(item.discountAmount)}</Typography>
                    </Stack>
                  )}
                </Stack>
              ))}
            </Stack>
            <Divider />
            <Stack
              sx={{
                gap: 0.5,
              }}
            >
              {showDiscount(invoice.discount) && (
                <Stack
                  sx={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="p3Regular">Total invoice discount</Typography>
                  <Typography data-testid="total-discount" variant="p3Regular">
                    -{format(invoice.discount)}
                  </Typography>
                </Stack>
              )}
              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="p2Medium">Total</Typography>
                <Typography variant="p2Medium">{format(invoice.net)}</Typography>
              </Stack>
            </Stack>
          </BorderedStack>
        </Stack>
      </Stack>
    </Dialog>
  )
}

const showDiscount = (amount: { value: number; currency: string }) => {
  return amount.value > 0
}

export const format = (amount: { value: number; currency: string }) => {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: amount.currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(amount.value)
}

const mapBillableIdToName = (billableId: string) => {
  switch (billableId) {
    case '1a8cdd9d-d2f0-4d89-8c91-63ea28512ab0':
      return 'Milage'
    case 'b6581f7f-e34a-4b77-8eea-f2de55e1a88a':
      return 'Business central'
    case 'ab32e409-e943-4f56-bde1-c634901ebad0':
      return 'Receipt matching'
    case 'c6679ffd-5604-4d67-b4b0-fa0bb97f4748':
      return 'Fixed fee'
    case '77294ae8-9a8f-409e-9f52-d1c79b401c23':
      return 'VismaNet'
    case '4251e606-8b73-479a-a2a6-f75be9658040':
      return 'Per diem'
    case 'b50f5512-d09a-4bef-bf55-f5f6871e47c3':
      return 'Users'
    case '1694f9f3-e21c-49e3-9847-1319820f07d2':
      return 'Personal cards'
    case 'f495ce7e-fb0d-427e-8ff0-b182d1caa4cc':
      return 'Outlay'
    case '7bc92d04-7beb-492d-bba2-60919560ecc3':
      return 'Lounge Access Fee'
    default:
      return billableId
  }
}
